import { InlineNotification, Stack, TextInput } from "@carbon/react";
import { capitalize } from "@flaminem/utils";
import React from "react";
import {
  FieldErrors,
  FieldValue,
  FieldValues,
  FormProvider,
  SubmitHandler,
  useForm,
} from "react-hook-form";

import { ConfirmActionModal, PassiveModal } from "src/components/Modal";
import { useToast } from "src/components/Toasts/ToastContainer.tsx";
import { mapToModalError } from "src/errors";
import { PropertyValueInput } from "src/graphql/anti-fraud/__generated__/graphql.ts";
import { createBlacklistElementMutation } from "src/graphql/anti-fraud/queries/blacklists/mutations/CreateBlacklistElementMutation";
import { updateBlacklistElementMutation } from "src/graphql/anti-fraud/queries/blacklists/mutations/UpdateBlacklistElementMutation.ts";
import { usePerformMutation } from "src/graphql/utils";
import { useT } from "src/internationalization";
import { BlacklistElementRecord } from "src/pages/Blacklists/blacklist.mapping.ts";
import { useListSchema } from "src/pages/Blacklists/useListSchema.ts";
import { Property } from "src/types/property";

export type BlacklistElementModalProps = {
  onClose: () => void;
  onSuccess: () => void;
  item?: BlacklistElementRecord;
};

export const BlacklistElementModal: React.FC<BlacklistElementModalProps> = ({
  onClose,
  onSuccess,
  item,
}) => {
  const { listSchema } = useListSchema();
  const { showSuccessToast } = useToast();
  const t = useT();

  const isCreateMode = !item;

  const methods = useForm<Record<string, string>>({
    defaultValues: isCreateMode
      ? {}
      : listSchema?.properties.reduce(
          (acc, prop) => ({
            ...acc,
            [prop.code]: item[prop.name] ?? "",
          }),
          {}
        ),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = methods;

  const {
    performMutation: createElement,
    loading: createLoading,
    error: createError,
  } = usePerformMutation(createBlacklistElementMutation, {
    onSuccess: () => {
      showSuccessToast(t("page.blacklists.modal.create.toast.success.message"));
      onSuccess();
      onClose();
    },
  });

  const {
    performMutation: updateElement,
    loading: updateLoading,
    error: updateError,
  } = usePerformMutation(updateBlacklistElementMutation, {
    onSuccess: () => {
      showSuccessToast(t("page.blacklists.modal.update.toast.success.message"));
      onSuccess();
      onClose();
    },
  });

  const loading = isCreateMode ? createLoading : updateLoading;
  const error = isCreateMode ? createError : updateError;

  const onSubmit: SubmitHandler<Record<string, string>> = async (data) => {
    if (!listSchema) {
      console.error("listSchema is expected to be defined, but is not");
      return;
    }

    const payload: PropertyValueInput[] = Object.keys(data).map((key) => ({
      code: key,
      value: data[key],
    }));

    if (isCreateMode) {
      await createElement({ properties: payload, blacklistCode: listSchema.code });
    } else {
      await updateElement({
        id: item.id,
        properties: payload.filter((p) => !item.sourceProperties.includes(p.code)),
      });
    }
  };

  const getInvalidText = (errors: FieldErrors<FieldValue<FieldValues>>, field: string) => {
    switch (errors[field]?.type) {
      case "required":
        return t("page.blacklists.modal.create.form.field.invalid");
      case "constraint":
        return (
          errors[field]?.message ?? t("page.blacklists.modal.create.form.field.invalid.format")
        ).toString();
      default:
        return;
    }
  };

  const getInputFromProperty = (prop: Property) => {
    switch (prop.dataType) {
      case "string": {
        const isDisabled: boolean = !isCreateMode && item.sourceProperties.includes(prop.code);

        return (
          <TextInput
            key={prop.code}
            id={prop.code}
            data-input
            labelText={capitalize(prop.code)}
            invalid={!!errors?.[prop.code]}
            invalidText={getInvalidText(errors, prop.code)}
            disabled={isDisabled}
            {...register(prop.code, {
              required: {
                value: prop.mandatory,
                message: t("page.blacklists.modal.create.form.field.invalid"),
              },
              validate: {
                constraint: (val) =>
                  prop.constraints.every((constraint) =>
                    RegExp(constraint.value ?? "").test(val)
                  ) ||
                  prop.constraints
                    .map((c) => c.message)
                    .join(
                      " " + t("page.blacklists.modal.create.form.field.constraints.divider") + " "
                    ),
              },
            })}
          />
        );
      }
    }
  };

  if (!listSchema) {
    console.error("listSchema is expected to be defined, but is not");
    return null;
  }

  // Empty state check only for update mode
  if (!isCreateMode) {
    const isEmptyState = listSchema.properties.every((prop) =>
      item.sourceProperties.includes(prop.code)
    );

    if (isEmptyState) {
      return (
        <PassiveModal
          size="sm"
          title={t("page.blacklists.modal.update.title", {
            type: listSchema.name,
            value: item[listSchema.properties[0].code],
          })}
          onClose={onClose}
        >
          <FormProvider {...methods}>
            <Stack gap={5}>
              <InlineNotification kind="warning" hideCloseButton>
                {t("page.blacklists.modal.create.form.emptyState")}
              </InlineNotification>
              {listSchema.properties.map((prop) => {
                return getInputFromProperty(prop);
              })}
            </Stack>
          </FormProvider>
        </PassiveModal>
      );
    }
  }

  return (
    <ConfirmActionModal
      size="sm"
      error={mapToModalError(error)}
      title={
        isCreateMode
          ? t("page.blacklists.modal.create.title")
          : t("page.blacklists.modal.update.title", {
              type: listSchema.name,
              value: item[listSchema.properties[0].name],
            })
      }
      subtitle={!isCreateMode ? item[listSchema.properties[0].name] : undefined}
      loading={loading}
      onClose={onClose}
      onConfirm={handleSubmit(onSubmit)}
      confirmButtonText={t("general.actions.confirm")}
      initialFocus={
        isCreateMode ? "[data-input]:first-child" : "[data-input]:not(:disabled):first-child"
      }
      confirmButtonDisabled={!isCreateMode && !isDirty}
    >
      <FormProvider {...methods}>
        <Stack gap={5}>
          {listSchema.properties.map((prop) => {
            return getInputFromProperty(prop);
          })}
        </Stack>
      </FormProvider>
    </ConfirmActionModal>
  );
};
